import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut,
  Search,
  FileText,
  Eye,
  Users,
  Inbox,
  Send,
  Filter,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import {
  adminListApplications,
  adminStats,
  adminCsvUrl,
  getAdminEmail,
  getToken,
  clearSession,
} from "@/lib/api";

const MODALIDADES = [
  { key: "", label: "Todas las modalidades" },
  { key: "posdoctorado", label: "Posdoctorado" },
  { key: "estancia", label: "Estancia de Investigación" },
  { key: "estancia_conjunta", label: "Estancia Conjunta" },
];

const STATUS = [
  { key: "", label: "Todos los estados" },
  { key: "recibida", label: "Borrador" },
  { key: "enviada", label: "Enviada" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterModalidad, setFilterModalidad] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refresh(params = {}) {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        adminListApplications(params),
        adminStats(),
      ]);
      setItems(list);
      setStats(st);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearSession();
        navigate("/admin/login");
      } else {
        toast.error("Error al cargar las inscripciones");
      }
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase();
    return items.filter((it) => {
      if (filterModalidad && it.modalidad !== filterModalidad) {
        return false;
      }
      if (filterStatus && it.status !== filterStatus) {
        return false;
      }
      if (!q) return true;
      const fullName = `${it.nombre} ${it.apellido_paterno} ${it.apellido_materno || ""}`.toLowerCase();
      return (
        it.folio.toLowerCase().includes(q) ||
        it.email.toLowerCase().includes(q) ||
        fullName.includes(q)
      );
    });
  }, [items, filterModalidad, filterStatus, search]);

  function handleLogout() {
    clearSession();
    navigate("/admin/login");
  }

  return (
    <div className="min-h-screen flex flex-col bg-eje/[0.03]">
      <Header variant="light" />

      <main className="container-wide py-10 flex-1">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="overline text-eje mb-2">Panel administrador</div>
            <h1
              className="font-serif text-3xl md:text-4xl text-eje"
              data-testid="admin-dashboard-title"
            >
              Inscripciones recibidas
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Sesión: {getAdminEmail()}
            </p>
          </div>
          <div className="flex items-center gap-2 self-start md:self-auto">
            <a
              href={adminCsvUrl()}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-eje text-eje rounded-sm hover:bg-eje hover:text-white transition-colors"
              data-testid="export-csv-button"
            >
              <Download className="w-4 h-4" /> Exportar CSV
            </a>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-slate-300 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" /> Cerrar sesión
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
            data-testid="admin-stats"
          >
            <StatCard
              icon={<Inbox />}
              label="Total"
              value={stats.total}
              testid="stat-total"
            />
            <StatCard
              icon={<Send />}
              label="Enviadas"
              value={stats.enviada}
              testid="stat-enviadas"
            />
            <StatCard
              icon={<FileText />}
              label="Borradores"
              value={stats.borrador}
              testid="stat-borradores"
            />
            <StatCard
              icon={<Users />}
              label="Posdoctorado"
              value={stats.by_modalidad?.posdoctorado || 0}
              testid="stat-posdoc"
            />
          </div>
        )}

        {/* Filters */}
        <div className="card-eje p-5 mb-6 flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              className="input-eje pl-10"
              placeholder="Buscar por folio, nombre o correo…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <Filter className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <select
                className="input-eje pl-10 pr-8 min-w-[200px]"
                value={filterModalidad}
                onChange={(e) => setFilterModalidad(e.target.value)}
                data-testid="filter-modalidad"
              >
                {MODALIDADES.map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <select
              className="input-eje min-w-[170px]"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              data-testid="filter-status"
            >
              {STATUS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div
          className="card-eje overflow-hidden"
          data-testid="applications-table"
        >
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-eje/5 border-b border-slate-200 text-left">
                <tr>
                  <Th>Folio</Th>
                  <Th>Aspirante</Th>
                  <Th>Correo</Th>
                  <Th>Modalidad</Th>
                  <Th>Estado</Th>
                  <Th>Docs.</Th>
                  <Th>Fecha</Th>
                  <Th className="text-right">Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-slate-500">
                      Cargando…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-slate-500">
                      No hay inscripciones que coincidan con los filtros.
                    </td>
                  </tr>
                ) : (
                  filtered.map((it) => (
                    <tr
                      key={it.id}
                      className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`application-row-${it.folio}`}
                    >
                      <Td>
                        <span className="font-medium text-eje">
                          {it.folio}
                        </span>
                      </Td>
                      <Td>
                        {it.nombre} {it.apellido_paterno} {it.apellido_materno}
                      </Td>
                      <Td className="text-slate-600">{it.email}</Td>
                      <Td className="capitalize text-slate-700">
                        {it.modalidad.replace(/_/g, " ")}
                      </Td>
                      <Td>
                        <StatusBadge status={it.status} />
                      </Td>
                      <Td className="text-center text-slate-700">
                        {it.documents_count}
                      </Td>
                      <Td className="text-slate-500 whitespace-nowrap">
                        {new Date(it.created_at).toLocaleDateString("es-MX")}
                      </Td>
                      <Td className="text-right">
                        <Link
                          to={`/admin/inscripciones/${it.id}`}
                          className="inline-flex items-center gap-1.5 text-eje hover:text-gold-600 transition-colors text-sm"
                          data-testid={`view-application-${it.folio}`}
                        >
                          <Eye className="w-4 h-4" /> Ver
                        </Link>
                      </Td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div className="card-eje p-5 flex items-center gap-4" data-testid={testid}>
      <div className="w-10 h-10 bg-eje/5 border border-eje/10 text-eje flex items-center justify-center rounded-sm [&>svg]:w-5 [&>svg]:h-5">
        {icon}
      </div>
      <div>
        <div className="overline text-slate-500">{label}</div>
        <div className="font-serif text-2xl text-eje">{value}</div>
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-4 py-3 text-xs uppercase tracking-wider text-slate-600 font-medium ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 ${className}`}>{children}</td>;
}

function StatusBadge({ status }) {
  const map = {
    enviada: "bg-emerald-50 text-emerald-700 border-emerald-200",
    recibida: "bg-amber-50 text-amber-700 border-amber-200",
  };
  const labels = { enviada: "Enviada", recibida: "Borrador" };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${
        map[status] || "bg-slate-50 text-slate-700 border-slate-200"
      }`}
    >
      {labels[status] || status}
    </span>
  );
}
